import {
    Typography,
    Stack,
    Button,
    Box,
    Grid,
    Paper,
    styled,
    Divider,
    MenuItem,
    ListItemIcon,
    useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import { Link as RouterLink, useNavigate, useParams } from "react-router";
import FullTaskLayout from "../../../Layouts/FullTask";
import { routePaths } from "../../../Router/router";
import FullTaskTopbar from "../../../Components/Topbars/FullTaskTopbar";
import MoreMenu from "../../../Components/Misc/MoreMenu";
import { orderService } from "../../../Core/Api/services";
import moment from "moment";
import DataTable, { CellSize } from "../../../Components/Table/table";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import TableCellMovie from "../../../Components/Table/TableCellMovie/table-cell-movie";
import TableCellOrderStatus from "../../../Components/Table/TableCellOrderStatus/table-cell-order-status";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { CurrencyPriceInfos } from "../../../Core/Api/Types/currency-price-infos";
import { GetOrderResponse } from "../../../Core/Api/Types/get-order-response";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import OrderProductIcon from "../../../Components/Misc/OrderProductIcon/order-product-icon";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { GetCurrencyChar, GetOrderProductReadableType } from "../../../Utils/helpers";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import { hexToRgbString } from "../../../Utils/Helpers/cssUtils";
import { useTranslation } from "react-i18next";

type DetailedPriceProps = {
    titleId: number | null;
    titleName: string | null;
    configuration: string | null;
    status: string | null;
    contract: string | null;
    price: CurrencyPriceInfos | string | null;
    type: string | null;
    summarizedInfos: string | null;
    isDetails: boolean | null;
};

export enum InvoiceContext {
    Order,
    Basket,
}

export interface OrderInvoiceProps {
    context: InvoiceContext;
}

const BasketSuccess: React.FC<OrderInvoiceProps> = (props) => {
    const { t } = useTranslation();
    const { context } = props;
    const [order, setOrder] = useState<GetOrderResponse>();
    const [items, setItems] = useState<DetailedPriceProps[]>([]);
    const theme = useTheme();
    const params = useParams();
    const orderId = parseInt(params.orderId!);
    const rows: GridRowsProp = items;
    type Row = (typeof rows)[number];
    const navigate = useNavigate();

    useEffect(() => {
        orderService.GetOrderById(orderId).then((response) => {
            setOrder(response);
            if (response.order?.items !== undefined && response.order?.items.length > 0) {
                let mappedItems: DetailedPriceProps[] = response.order.items.map((item, index) => ({
                    id: index,
                    titleId: item.relatedTitleId || -1,
                    titleName: item.objectName || "-",
                    configuration: item.summarizedInfos || "-",
                    status: item.status,
                    contract: "-",
                    price: item.price,
                    type: item.type,
                    summarizedInfos: item.summarizedInfos,
                    isDetails: false,
                }));
                setItems(mappedItems);
            }
        });
    }, []);

    const columns: GridColDef[] = [
        {
            field: "titleName",
            headerName: t("common:title"),
            flex: CellSize.xxlarge,
            minWidth: CellSize.xxlarge,
            renderCell: (data: Row) => {
                return !data?.row?.isDetails ? <TableCellMovie id={data?.row?.titleId} name={data?.row?.titleName} /> : "";
            },
        },
        {
            field: "summarizedInfos",
            headerName: t("common:configuration"),
            flex: CellSize.xxlarge,
            minWidth: CellSize.xxlarge,
            renderCell: (data: Row) => {
                return !data?.row?.isDetails ? (
                    <CellOrderItem
                        title={GetOrderProductReadableType(data?.row?.type)}
                        desc={data?.row?.summarizedInfos}
                        type={data?.row?.type}
                    />
                ) : (
                    <Typography variant="body2" color="text.tier">
                        {data?.row?.configuration}
                    </Typography>
                );
            },
        },
        {
            field: "status",
            headerName: t("common:status"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: Row) => (!data?.row?.isDetails ? <TableCellOrderStatus data={data} /> : ""),
        },
        //TODO : the information we have here is contractExternalDealId
        // {
        //   field: "3",
        //   headerName: "Contract",
        //   flex: 100,
        //   renderCell: (data: Row) => "-",
        // },
        {
            field: "price",
            headerName: t("common:price"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            align: "right",
            headerAlign: "right",
            renderCell: (data: Row) =>
                !data?.row?.isDetails ? (
                    <Typography pr={1}>
                        {data.value.price} {GetCurrencyChar(data.value.currency)}
                    </Typography>
                ) : (
                    <StyledStack>
                        <Typography variant="body2" color="text.tier">
                            {data.value.price} {GetCurrencyChar(data.value.currency)}
                        </Typography>
                    </StyledStack>
                ),
        },
    ];

    return (
        <FullTaskLayout
            withContainer
            TopBar={
                <FullTaskTopbar
                    title={
                        order ? t("common:orderWithReference", { reference: order.order?.reference }) : GlobalConst.LOADING_TEXT
                    }
                    rightContent={
                        context === InvoiceContext.Basket ? (
                            <MoreMenu>
                                <MenuItem onClick={() => navigate(routePaths.basket.path)}>Back to basket</MenuItem>
                            </MoreMenu>
                        ) : (
                            <></>
                        )
                    }
                    backRoute={routePaths.home.path}
                    closeOption={context === InvoiceContext.Order ? false : true}
                />
            }
            sxContainer={{
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {context === InvoiceContext.Basket && (
                <Grid container justifyContent="center" alignItems="center" mb={5} mt={3} displayPrint="none">
                    <Grid item md={4} textAlign="center">
                        <figure>
                            <img src={`/static/images/order-success-${theme.palette.mode}.svg`} alt="" />
                        </figure>
                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1.5} mt={4}>
                            <Typography component="h1" variant="h3" textAlign="center">
                                {t("page:body.orderSuccessful")}
                            </Typography>
                            <Box
                                width={24}
                                height={24}
                                borderRadius={24}
                                bgcolor="success.main"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <CheckIcon sx={{ color: "white" }} fontSize="small" />
                            </Box>
                        </Stack>
                        <Typography
                            variant="subtitle2"
                            component="p"
                            color="text.tier"
                            mt={2.5}
                            textAlign="center"
                            fontWeight={400}
                        >
                            {t("page:body.youBeNotifiedByEmailWhenFilesReady")}
                        </Typography>
                        <Button
                            component={RouterLink}
                            to={routePaths.ordersItemDetails.path}
                            color="primary"
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            sx={{ mt: 4 }}
                        >
                            {t("button:goToMyOrderList")}
                        </Button>
                    </Grid>
                </Grid>
            )}

            {order && (
                <Stack pb={5} pt={context === InvoiceContext.Order ? 5 : 0}>
                    <Paper elevation={0} sx={{ backgroundImage: "none", padding: "20px 24px", borderRadius: "12px" }}>
                        <Stack direction="row" justifyContent="space-between">
                            <Stack>
                                <Typography variant="h5" component="h2" mb={0.5}>
                                    {t("common:orderWithReference", { reference: order.order?.reference })}
                                </Typography>
                                <Typography variant="body2" color="text.tier" mb={2}>
                                    {moment(order.order?.creationTime).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
                                </Typography>
                            </Stack>
                            <Button size="small" sx={{ height: 35 }} startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>
                                {t("button:printOrderSummary")}
                            </Button>
                        </Stack>

                        <Stack direction="row" justifyContent="flex-start" spacing={5} mb={3}>
                            <Stack flexBasis={"50%"}>
                                <Typography component="span" variant="h6" fontWeight={500} color="text.tier" mb={1}>
                                    {t("common:fromUpperCaseAll")}
                                </Typography>

                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Typography variant="body1" mt={-0.1}>
                                        {order.invoiceFrom?.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.tier">
                                        {order.invoiceFrom?.address || ""} {order.invoiceFrom?.city && `${order.invoiceFrom?.city}, `}
                                        {order.invoiceFrom?.zipCode || ""}
                                    </Typography>
                                </Stack>
                            </Stack>

                            <Stack flexBasis={"50%"}>
                                <Typography component="span" variant="h6" fontWeight={500} color="text.tier" mb={1}>
                                    {t("common:toUpperCaseAll")}
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Typography variant="body1" mt={-0.1}>
                                        {order.invoiceTo?.name}{" "}
                                    </Typography>
                                    <Typography variant="body2" color="text.tier">
                                        {order.invoiceTo?.address} {order.invoiceFrom?.city && `${order.invoiceFrom?.city}, `}
                                        {order.invoiceTo?.zipCode}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                        <Divider sx={{ mt: 2, mb: 3 }} />

                        <Typography variant="subtitle2" mb={2}>
                            {t("page:body.itemWithCount", { count: order.order?.items.length || 0 })}
                        </Typography>

                        <DataTable
                            noControls
                            rows={rows}
                            columns={columns}
                            columnHeaderHeight={48}
                            getRowHeight={() => "auto"}
                            pageSize={100}
                            hideFooter={false}
                            slots={{
                                footer: () => (
                                    <Footer>
                                        <Stack direction="row" width="fit-content" sx={{ float: "right", pr: "20px" }} spacing={2}>
                                            <Typography color="text.tier">{t("page:body.totalPrice")}:</Typography>
                                            {order.order?.totalPrices !== null && order.order!.totalPrices!.length > 0 && (
                                                <Typography>
                                                    {order.order?.totalPrices[0].price} {GetCurrencyChar(order.order?.totalPrices[0].currency!)}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Footer>
                                ),
                            }}
                            {...{
                                getDetailPanelContent: (params) => {
                                    if (params?.row?.price.priceDetails !== null)
                                        return (
                                            <PriceDetailTable row={params.row} columns={columns} currency={params?.row?.price.currency} />
                                        );
                                },
                                getDetailPanelHeight: (params) =>
                                    params?.row?.price.priceDetails === null ? 0 : 40 * params?.row?.price.priceDetails.length,
                            }}
                        />

                        <Stack direction="row" justifyContent="flex-start" spacing={5} mt={3}>
                            <Stack flexBasis={"50%"}>
                                <Typography variant="body2" mb={1}>
                                    {t("page:body.remarks")}
                                </Typography>
                                <Typography variant="body2" color="text.tier">
                                    {order.remarks}
                                </Typography>
                            </Stack>
                            <Stack flexBasis={"50%"}>
                                <Typography variant="body2" mb={1}>
                                    {t("page:body.reclamations")}
                                </Typography>
                                <Typography variant="body2" color="text.tier">
                                    {t("page:body.contactDirectltForReclamation", {
                                        name: order.invoiceFrom?.name,
                                        email: order.invoiceFrom?.email,
                                        context: order.invoiceFrom?.email ? "withEmail" : "",
                                    })}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                </Stack>
            )}
            {!order && (
                <Stack pt={context === InvoiceContext.Order ? 5 : 0}>
                    <TableSkeleton />
                </Stack>
            )}
        </FullTaskLayout>
    );
};

export default BasketSuccess;

const PriceDetailTable: React.FC<{ row: DbOrderItemInfos; columns: GridColDef[]; currency: string }> = (props) => {
    const [rows, setRows] = useState<DetailedPriceProps[]>([]);
    useEffect(() => {
        if (props.row.price !== null && props.row.price.priceDetails !== null) {
            let rows: DetailedPriceProps[] = props.row.price.priceDetails.map((item, index) => ({
                id: index,
                titleId: -1,
                titleName: "-",
                configuration: item.comment || "-",
                status: "-",
                contract: "-",
                price: item.price + " " + props.currency,
                type: "-",
                summarizedInfos: "-",
                isDetails: true,
            }));
            setRows(rows);
        } else setRows([]);
    }, []);

    if (rows.length > 0)
        return (
            <StyledDetails>
                <DataTable
                    noControls
                    rows={rows}
                    columns={props.columns}
                    columnHeaderHeight={0}
                    rowHeight={40}
                    getRowHeight={() => "auto"}
                    pageSize={20}
                />
            </StyledDetails>
        );
    return null;
};

const Footer = styled(Box)(({ theme }) => ({
    paddingLeft: 48,
    background: theme.palette.backgroundAlpha.pressed,
    borderBottomLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    height: "50px",
    paddingTop: "18px !important",
}));

const StyledDetails = styled(Box)(({ theme }) => ({
    background: theme.palette.background.paper,
    paddingLeft: 50,
    ".MuiDataGrid-columnHeaders": {
        border: "none",
    },
    ".MuiDataGrid-cell-id": {
        borderColor: "transparent",
    },
    ".MuiDataGrid-virtualScroller": {
        borderRadius: 0,
        border: "none",
    },
    ".MuiDataGrid-row": {},
}));

const StyledStack = styled(Stack)(({ theme }) => ({
    width: "-webkit-fill-available",
    background: `linear-gradient(270deg, ${theme.palette.background.default} 0%, rgba(14, 14, 27, 0) 100%)`,
    padding: "10px",
    marginRight: "-10px",
}));

export type CellOrderItemProps = {
    title: string;
    desc: string;
    type: OrderProductType;
};

const CellOrderItem: React.FC<CellOrderItemProps> = (props) => {
    const { title, desc, type } = props;
    return (
        <StyledCellOrder spacing={1} direction="row" alignItems="center" position="relative">
            <OrderProductIcon type={type} sx={{ flexShrink: 0 }} color="info" />
            <Stack overflow="hidden">
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5} pr={3}>
                    <Typography
                        variant="body1"
                        title={title}
                        fontWeight={500}
                        sx={(theme) => ({
                            ...theme.helpers(theme).ellipsis,
                        })}
                    >
                        {title}
                    </Typography>
                </Stack>
                <Typography title={desc} variant="body2" color="text.tier">
                    {desc}
                </Typography>
            </Stack>
        </StyledCellOrder>
    );
};

const StyledCellOrder = styled(Stack)(({ theme }) => ({
    position: "relative",
    height: "100%",
    width: "100%",
    overflow: "hidden",
    "&:after": {
        content: '""',
        position: "absolute",
        right: 0,
        top: 0,
        bottom: 0,
        width: 40,
        background: `linear-gradient(270deg, ${theme.palette.background.paper} 0%, rgba(${hexToRgbString(
            theme.palette.background.paper
        )}, 0) 100%)`,
    },
    ".MuiChip-root": {
        position: "relative",
        zIndex: 1,
    },
}));
